import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';
import { tableColumns, TAB_TYPES, formatDate } from './HomeScreenConfigData';
import CreateHomeScreenConfig from './CreateHomeScreenConfig';
import EditHomeScreenConfig from './EditHomeScreenConfig';

// Same SVG palette as LearningTable
import EditIcon from '../../assets/images/svg/editLearning.svg';
import PublishIcon from '../../assets/images/svg/publishLearning.svg';
import ArchiveIcon from '../../assets/images/svg/archiveLearning.svg';
import ArchiveEnabled from '../../assets/images/svg/archiveEnabled.svg';
import Search from '../../assets/images/svg/Search_SoS.svg';
import SearchIcon from '../../assets/images/svg/search_iconSos.svg';
import Filter from '../../assets/images/svg/Filter_SoS.svg';

const tableStyles = `
  .rdt_TableCell { padding: 4px 2px !important; }
  .rdt_TableHead { padding: 4px 2px !important; }
  .rdt_TableCol { padding: 4px 2px !important; }
  .rdt_TableCell[data-column-id="action"] {
    position: sticky !important; right: 0 !important;
    background-color: inherit !important; z-index: 1;
  }
  .rdt_TableHeadRow [data-column-id="action"] {
    position: sticky !important; right: 0 !important;
    background-color: inherit !important; z-index: 1;
  }
  .data-tables { overflow-x: auto; }
  .btn[title] { position: relative; }
  .btn[title]:hover:after {
    content: attr(title);
    position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
    padding: 4px 8px; color: white; background: #333; border-radius: 4px;
    font-size: 12px; white-space: nowrap; z-index: 10;
  }
  .btn-link:hover { text-decoration: none !important; }
  .btn[disabled] { cursor: not-allowed; }
`;

const ConfirmationModal = ({ show, onHide, onConfirm, title, message }) => (
  <Modal show={show} onHide={onHide} centered>
    <Modal.Body className="text-center p-4">
      <div className="mb-4">
        <div className="rounded-circle bg-warning bg-opacity-10 d-inline-flex p-4 mb-3">
          <i className="bi bi-exclamation-lg fs-4 text-warning"></i>
        </div>
        <h4>{title}</h4>
        <p className="text-secondary mb-0" style={{ whiteSpace: 'pre-line' }}>{message}</p>
      </div>
      <div className="d-flex gap-2 justify-content-center">
        <Button variant="primary" className="px-4" onClick={onConfirm}>Yes</Button>
        <Button variant="danger" className="px-4" onClick={onHide}>Cancel</Button>
      </div>
    </Modal.Body>
  </Modal>
);

const HomeScreenConfigTable = () => {
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [tabFilter, setTabFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editRowData, setEditRowData] = useState(null);
  const [confirm, setConfirm] = useState({ show: false, kind: null, row: null });

  const tabFilterOptions = [
    { label: 'All Tabs', value: '' },
    ...TAB_TYPES.map(t => ({ label: t, value: t }))
  ];
  const activeFilterOptions = [
    { label: 'All (Active + Inactive)', value: '' },
    { label: 'Active only', value: '1' },
    { label: 'Inactive only', value: '0' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) setShowSearch(false);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buildRow = (item) => ({
    id: item.id,
    tab_type: item.tab_type,
    seq_id: item.seq_id,
    heading: item.heading,
    category: item.category,
    category_desc: item.category_desc,
    active: item.active ? 1 : 0,
    updated_on: item.updated_on,
    updated_on_display: formatDate(item.updated_on),
    raw_data: item,
    action: (
      <div className="d-flex justify-content-center gap-2">
        <button
          className="btn btn-link p-0"
          title="Edit"
          onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
        >
          <img src={EditIcon} alt="edit" style={{ width: '24px', height: '24px' }} />
        </button>
        <div title={item.active ? 'Published' : 'Publish'}>
          <button
            className="btn btn-link p-0"
            disabled={!!item.active}
            onClick={(e) => {
              e.stopPropagation();
              if (!item.active) setConfirm({ show: true, kind: 'publish', row: item });
            }}
            style={{ opacity: item.active ? 0.5 : 1 }}
          >
            <img src={PublishIcon} alt="publish" style={{ width: '24px', height: '24px' }} />
          </button>
        </div>
        <div title={!item.active ? 'Archived' : 'Archive'}>
          <button
            className="btn btn-link p-0"
            disabled={!item.active}
            onClick={(e) => {
              e.stopPropagation();
              if (item.active) setConfirm({ show: true, kind: 'archive', row: item });
            }}
            style={{ opacity: !item.active ? 0.5 : 1 }}
          >
            <img
              src={!item.active ? ArchiveEnabled : ArchiveIcon}
              alt="archive"
              style={{ width: '24px', height: '24px' }}
            />
          </button>
        </div>
      </div>
    )
  });

  const fetchData = useCallback(async (page = currentPage, limit = perPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', limit);
      if (tabFilter) params.set('tab_type', tabFilter);
      if (activeFilter !== '') params.set('active', activeFilter);

      const res = await _axios.get(`${ServerPath}/home-screen-config/admin?${params.toString()}`);
      if (res.data && res.data.success) {
        const transformed = (res.data.data || []).map(item => buildRow(item));
        setData(transformed);
        setOriginalData(transformed);
        setTotalRows(res.data.total || transformed.length);
      } else {
        toast.error(res.data?.message || 'Failed to load rows');
      }
    } catch (err) {
      console.error('fetchData error:', err);
      toast.error('Failed to fetch home_screen_config rows');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, tabFilter, activeFilter]);

  useEffect(() => {
    setCurrentPage(1);
    fetchData(1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFilter, activeFilter]);

  useEffect(() => {
    fetchData(currentPage, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, perPage);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchData(page, newPerPage);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    if (!value) {
      setData(originalData);
      return;
    }
    const q = value.toLowerCase();
    setData(originalData.filter(r =>
      (r.heading || '').toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q) ||
      (r.category_desc || '').toLowerCase().includes(q) ||
      String(r.id).includes(q)
    ));
  };

  const handleEdit = (item) => {
    setEditRowData(item);
    setShowEdit(true);
  };

  const handleCreateClose = (shouldRefresh = false) => {
    setShowCreate(false);
    if (shouldRefresh) fetchData(currentPage, perPage);
  };

  const handleEditClose = (shouldRefresh = false) => {
    setShowEdit(false);
    setEditRowData(null);
    if (shouldRefresh) fetchData(currentPage, perPage);
  };

  const handleConfirm = async () => {
    const { kind, row } = confirm;
    if (!row) return;
    try {
      const res = await _axios.patch(`${ServerPath}/home-screen-config/admin/${row.id}/toggle-active`);
      if (res.data?.success) {
        toast.success(kind === 'publish' ? 'Row published' : 'Row archived');
      } else {
        toast.error(res.data?.message || 'Operation failed');
      }
      setConfirm({ show: false, kind: null, row: null });
      fetchData(currentPage, perPage);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Operation failed');
      setConfirm({ show: false, kind: null, row: null });
    }
  };

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Container fluid={true}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Home Screen Config</h3>
          <div className="d-flex align-items-center gap-3">
            {/* Search */}
            <div className="position-relative" ref={searchRef}>
              <button className="bg-transparent border-0" onClick={() => setShowSearch(true)}>
                <img src={Search} alt="search" style={{ width: '36px', height: '36px' }} />
              </button>

              {showSearch && (
                <div style={{ position: 'absolute', top: '45px', left: '-180px', width: '220px', zIndex: 1000 }}>
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      background: '#F8F7FA',
                      borderRadius: '6px',
                      padding: '6px 12px'
                    }}>
                      <input
                        type="text"
                        placeholder="Search Here..."
                        value={searchValue}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{
                          width: '100%',
                          border: 'none',
                          background: 'transparent',
                          fontSize: '14px',
                          color: '#666',
                          outline: 'none'
                        }}
                        autoFocus
                      />
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onClick={() => handleSearch(searchValue)}
                      >
                        <img src={SearchIcon} alt="search" style={{ width: '20px', height: '20px' }} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Filter (Tab + Status) */}
            <div className="position-relative" ref={dropdownRef}>
              <button className="bg-transparent border-0" onClick={() => setShowDropdown(!showDropdown)}>
                <img src={Filter} alt="filter" style={{ width: '36px', height: '36px' }} />
              </button>

              {showDropdown && (
                <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-3"
                     style={{ width: '240px', zIndex: 1000 }}>
                  <div className="mb-2">
                    <div className="fw-bold small mb-1">Tab</div>
                    <Form>
                      {tabFilterOptions.map((option) => (
                        <Form.Check
                          key={`tab-${option.value}`}
                          type="radio"
                          name="tabFilter"
                          id={`tab-${option.value || 'all'}`}
                          label={option.label}
                          checked={tabFilter === option.value}
                          onChange={() => setTabFilter(option.value)}
                        />
                      ))}
                    </Form>
                  </div>
                  <hr className="my-2" />
                  <div>
                    <div className="fw-bold small mb-1">Status</div>
                    <Form>
                      {activeFilterOptions.map((option) => (
                        <Form.Check
                          key={`active-${option.value}`}
                          type="radio"
                          name="activeFilter"
                          id={`active-${option.value || 'all'}`}
                          label={option.label}
                          checked={activeFilter === option.value}
                          onChange={() => setActiveFilter(option.value)}
                        />
                      ))}
                    </Form>
                  </div>
                </div>
              )}
            </div>

            {/* Create */}
            <Button variant="primary" onClick={() => setShowCreate(true)} className="bg-primary">
              Create
            </Button>
          </div>
        </div>

        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <DataTable
                  columns={tableColumns}
                  data={data}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationPerPage={perPage}
                  paginationRowsPerPageOptions={[10, 20, 50, 100]}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                  progressPending={loading}
                  highlightOnHover
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <CreateHomeScreenConfig show={showCreate} onHide={handleCreateClose} />
      <EditHomeScreenConfig show={showEdit} onHide={handleEditClose} rowData={editRowData} />
      <ConfirmationModal
        show={confirm.show}
        onHide={() => setConfirm({ show: false, kind: null, row: null })}
        onConfirm={handleConfirm}
        title="Are you sure?"
        message={
          confirm.kind === 'publish'
            ? "You want to set this row to 'Active'?\nThis will publish it and make it visible in the app."
            : "You want to archive this row? Once archived,\nit will no longer be visible in the app."
        }
      />
    </Fragment>
  );
};

export default HomeScreenConfigTable;
